import * as React from 'react';
import * as Scroll from 'react-scroll';
import Loader from 'components/uielements/Loader';
import HighlightCard from 'components/HighlightCard/index';
import { getContentTypeDisplayName } from 'utils/string/index';
import type { SearchResultsListProps } from './SearchResultsList.types';
import SearchResultsListWrapper, { StyledText, StyledCountText } from './SearchResultsList.style';

const { animateScroll } = Scroll;

const SearchResultsList = ({
  searchResults,
  isLoading,
  onClickViewAll,
  activeFilter,
  totalCount,
  counts,
}: SearchResultsListProps) => {
  const getSubtitleText = (contentType: string): string => {
    switch (contentType) {
      case 'school':
        return 'Get the facts on any US based university or college.';

      case 'program_type':
        return 'Pick any program, and use our resources to become an expert on it.';

      case 'partner_programs':
        return 'Pick any degree program, and see the program content.';

      default:
        return '';
    }
  };

  const renderSectionCount = (contentType: string): React.ReactElement<any> | null => {
    const containsKey = counts && Object.keys(counts).find((key) => key === contentType);

    if (!containsKey || activeFilter) {
      return null;
    }

    const count = counts[contentType];
    const title = getContentTypeDisplayName(contentType);

    if (!count) {
      return null;
    }

    return (
      <StyledCountText>
        {count} {title}
      </StyledCountText>
    );
  };

  const renderViewAllBtn = (
    contentType: string,
    viewAllLimit: number,
    isActiveFilter: boolean,
  ): React.ReactElement<any> | null => {
    // Avoid crashing app if unexpected item type
    if (!contentType) {
      console.error('contentType is undefined');
      return null;
    }

    return counts[contentType] >= viewAllLimit && !isActiveFilter ? (
      <button
        type='button'
        className='search-results-list__view-all-btn'
        onClick={() => {
          onClickViewAll(contentType);
          animateScroll.scrollToTop({
            duration: 300,
          });
        }}
      >
        <StyledText>View All</StyledText>
      </button>
    ) : null;
  };

  const searchResultsListRender = (): React.ReactElement<any>[] | null => {
    if (!totalCount) {
      return null;
    }

    const filteredSearchResults: Record<string, any> = activeFilter
      ? {
          [activeFilter]: searchResults[activeFilter],
        }
      : searchResults;
    const filteredSearchResultsArr = Object.entries(filteredSearchResults);
    const orderedGroupedSearchResultsArr = filteredSearchResultsArr.sort((a: any, b: any) => {
      const sectionOrder = [
        'course',
        'partner_programs',
        'area_of_interest',
        'program_type',
        'school',
      ];
      const aOrder = sectionOrder.indexOf(a[0]);
      const bOrder = sectionOrder.indexOf(b[0]);
      return aOrder - bOrder;
    });
    return orderedGroupedSearchResultsArr.map(([key, value]: any): any => {
      let viewAllLimit = 5;
      let processedSearchResults = value;
      const displayName = getContentTypeDisplayName(key);
      const contentType: string = key;
      const isActiveFilter = activeFilter === contentType;

      // Limiting program types to only 2, since doing it on the BE won't be possible.
      if (key === 'program_type' && !activeFilter) {
        viewAllLimit = 2;
        processedSearchResults = value.slice(0, 2);
      }

      return displayName !== 'More' || processedSearchResults ? (
        <div className='search-results-list__section-container'>
          <div className='search-results-list__info-container'>
            <div className='search-results-list__header__container'>
              <h3 className='search-results-list__header__text'>{displayName}</h3>
            </div>
            <p className='search-results-list__subheader'>{getSubtitleText(key)}</p>
          </div>
          <div className='search-results-list__results-container'>
            {processedSearchResults.map((item) => (
              <HighlightCard key={item?.slug} itemData={item} contentType={contentType} />
            ))}
          </div>
          {renderViewAllBtn(contentType, viewAllLimit, isActiveFilter)}
          {renderSectionCount(contentType)}
        </div>
      ) : null;
    });
  };

  return isLoading ? (
    <Loader top='40vh' />
  ) : (
    <SearchResultsListWrapper>{searchResultsListRender()}</SearchResultsListWrapper>
  );
};

export default SearchResultsList;
